import { supabase } from "../supabaseClient";

/**
 * Upload an image directly from the browser to Cloudflare R2.
 *
 * Flow:
 * 1. Ask our authenticated Supabase Edge Function for a short-lived R2 upload URL.
 * 2. Upload the actual image bytes directly from the browser to R2.
 * 3. Return storage-neutral metadata for item_images.
 *
 * The image bytes never pass through Supabase Storage.
 */
export async function uploadImageToR2(file) {
  if (!file) {
    throw new Error("uploadImageToR2 requires a file");
  }

  if (!file.type) {
    throw new Error("Image file is missing a MIME type");
  }

  if (!file.size || file.size <= 0) {
    throw new Error("Image file is empty");
  }

  // Ask our trusted backend to authorize this upload.
  const { data, error } = await supabase.functions.invoke(
    "create-r2-upload",
    {
      body: {
        contentType: file.type,
        fileSize: file.size,
      },
    }
  );

  if (error) {
    console.error("Failed to authorize R2 upload:", error);
    throw error;
  }

  if (!data?.success || !data?.uploadUrl || !data?.storage?.objectKey) {
    console.error("Invalid create-r2-upload response:", data);
    throw new Error(
      data?.error || "Unable to authorize image upload"
    );
  }

  // Actual image bytes go directly:
  // Browser -> Cloudflare R2
  const uploadResponse = await fetch(data.uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type,
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    const responseText = await uploadResponse
      .text()
      .catch(() => "");

    console.error(
      "R2 upload failed:",
      uploadResponse.status,
      responseText
    );

    throw new Error(
      `R2 image upload failed (${uploadResponse.status})`
    );
  }

  return {
    storage_provider: data.storage.provider || "r2",
    storage_bucket: data.storage.bucket,
    object_key: data.storage.objectKey,
    mime_type: file.type,
    file_size_bytes: file.size,
    width: null,
    height: null,
  };
}